import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { getInvoiceMasterData, getPayoutMasterData } from './Receivables.actionsCreator';
import Calculations from './Calculations';
import TmsCarrierReceivables from './Invoices';
import Navbar from 'src/common/Navbar/Navbar';
import { RESET_RECEIVABLES_DATA, SET_RECEIVABLES_PAGINATION } from 'src/common/actions/constants';
import { CssType, ThemeType } from 'src/theme/jss-types';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';

interface Props {
  getInvoiceMasterData: () => void;
  resetReceivablesData: () => void;
  getPayoutMasterData: () => void;
  resetReceivablesPagination: () => void;
  t: (key: string) => string;
  loader: boolean;
  allowCreationOfInvoice: boolean;
  classes: Record<string, string>;
}

const ReceivablesContainer: React.FC<Props> = ({
  getInvoiceMasterData,
  resetReceivablesData,
  resetReceivablesPagination,
  getPayoutMasterData,
  t,
  loader,
  allowCreationOfInvoice,
  classes,
}) => {
  const [activeTab, setActiveTab] = useState('calculations');
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    resultPerPage: 10,
    nextOrPrev: 'first',
    isNextPresent: false,
  });

  useEffect(() => {
    getInvoiceMasterData();
    getPayoutMasterData();
  }, []);

  const handleTabChange = (key: string) => {
    resetReceivablesData();
    setActiveTab(key);
    resetReceivablesPagination();
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.navbarWrapper}>
          <Navbar title={t('Receivables')} />
        </div>
        <div className={classes.customTabsWrapper}>
          {allowCreationOfInvoice && (
            <div
              className={classNames(classes.customTab, {
                [classes.customTabActive]: activeTab === 'invoices',
              })}
              onClick={loader ? () => {} : () => handleTabChange('invoices')}
              role="tab"
              tabIndex={0}
              onKeyPress={(e) => e.key === 'Enter' && handleTabChange('invoices')}
            >
              <p
                className={classNames(classes.customTabText, {
                  [classes.customTabTextActive]: activeTab === 'invoices',
                })}
              >
                INVOICES
              </p>
            </div>
          )}
          <div
            className={classNames(classes.customTab, {
              [classes.customTabActive]: activeTab === 'calculations',
            })}
            onClick={loader ? () => {} : () => handleTabChange('calculations')}
            role="tab"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && handleTabChange('calculations')}
          >
            <p
              className={classNames(classes.customTabText, {
                [classes.customTabTextActive]: activeTab === 'calculations',
              })}
            >
              CALCULATIONS
            </p>
          </div>
        </div>
      </div>
      <div className={classes.contentWrapper}>
        <div
          className={classNames(classes.tabContent, {
            [classes.tabContentHidden]: activeTab !== 'calculations',
          })}
        >
          <Calculations
            activeTab={activeTab}
            resetReceivablesData={resetReceivablesData}
            pagination={pagination}
            setPagination={setPagination}
          />
        </div>
        {allowCreationOfInvoice && (
          <div
            className={classNames(classes.tabContent, {
              [classes.tabContentHidden]: activeTab !== 'invoices',
            })}
          >
            <TmsCarrierReceivables
              activeTab={activeTab}
              resetReceivablesData={resetReceivablesData}
              pagination={pagination}
              setPagination={setPagination}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ReceivablesContainerStyles = (theme: ThemeType): CssType => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: '#f5f4f2',
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    borderBottom: '1px solid #dddddd',
  },
  navbarWrapper: {
    height: '48px',
  },
  customTabsWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: '40px',
    backgroundColor: '#fff',
    borderTop: '1px solid #cccccc',
  },
  customTab: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '11px 24px',
    height: '39px',
    backgroundColor: '#fff',
    borderLeft: '1px dashed #cccccc',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  customTabActive: {
    cursor: 'default',
    pointerEvents: 'none',
  },
  customTabText: {
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    color: '#999999',
    textTransform: 'uppercase',
    margin: 0,
  },
  customTabTextActive: {
    fontWeight: 600,
    color: '#1659cb',
  },
  contentWrapper: {
    flex: 1,
    padding: 0,
    margin: 0,
  },
  tabContent: {
    display: 'block',
  },
  tabContentHidden: {
    display: 'none',
  },
});

const mapStateToProps = (state: any) => ({
  loader: state.receivables?.loader || false,
  allowCreationOfInvoice: !state.receivables?.hideVendorInvoiceTab,
});

const mapDispatchToProps = (dispatch: any) => ({
  getInvoiceMasterData: () => dispatch(getInvoiceMasterData()),
  getPayoutMasterData: () => dispatch(getPayoutMasterData()),
  resetReceivablesData: () => dispatch({ type: RESET_RECEIVABLES_DATA }),
  resetReceivablesPagination: () => dispatch({
    type: SET_RECEIVABLES_PAGINATION,
    data: {
      pageNumber: 1,
      resultPerPage: 10,
      nextOrPrev: 'first',
      isNextPresent: false,
    },
  }),
});

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: ReceivablesContainerStyles,
  },
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(ReceivablesContainer);
